import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useCalculationResults } from "src/hooks/results";
import { Selection } from "src/types";
import {
  getScenarios,
  getSubstances,
  getCalculations,
  validateCalculationSelection,
  validateSubstanceSelection,
} from "src/utils/case-result.utils";

interface Props {
  caseId: number | string;
  selection: Selection;
  onSelectionChange: (newSelection: Selection) => void;
}

export function CaseResultSelection(props: Props) {
  const { selection } = props;
  const { t } = useTranslation();
  const { data: calculationResults } = useCalculationResults(props.caseId);

  /**
   * Functions
   */

  function handleChangeSelection(selectionKey: keyof Selection, id: string) {
    if (!calculationResults) return;

    const changedSelection = { ...selection, [selectionKey]: id };

    const selectionValidatedForSubstance = validateSubstanceSelection(calculationResults, changedSelection);

    const selectionValidatedForCalculation = validateCalculationSelection(
      calculationResults,
      selectionValidatedForSubstance
    );
    props.onSelectionChange(selectionValidatedForCalculation);
  }

  // render the component
  return render();

  function render() {
    if (!calculationResults) return null;

    return (
      <Grid className="grid-container" container spacing={3}>
        <Grid className="grid-item" item xs={12} flex-basis="auto">
          <FormControl component="fieldset" variant="outlined" fullWidth>
            <FormLabel component="legend">{t("label.scenario")}</FormLabel>
            <RadioGroup
              aria-label="scenario"
              name="scenario"
              value={selection.scenario}
              onChange={(_event, value) => {
                handleChangeSelection("scenario", value);
              }}
            >
              {getScenarios(calculationResults).map((scenario) => (
                <FormControlLabel
                  key={scenario.id}
                  value={scenario.id.toString()}
                  control={<Radio color="primary" />}
                  label={scenario.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid className="grid-item" item xs={12}>
          <FormControl component="fieldset" variant="outlined" fullWidth>
            <FormLabel component="legend">{t("label.pollutant")}</FormLabel>
            <RadioGroup
              aria-label="substance"
              name="substance"
              value={selection.substance}
              onChange={(event, value) => {
                handleChangeSelection("substance", value);
              }}
            >
              {getSubstances(calculationResults, selection).map((substance) => (
                <FormControlLabel
                  key={substance.id}
                  value={substance.id.toString()}
                  control={<Radio color="primary" />}
                  label={<span dangerouslySetInnerHTML={{ __html: substance.name }} />}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid className="grid-item" item xs={12}>
          <FormControl component="fieldset" variant="outlined" fullWidth>
            <FormLabel component="legend">{t("label.calculations")}</FormLabel>
            <RadioGroup
              aria-label="calculation"
              name="calculation"
              value={selection.calculation}
              onChange={(event, value) => {
                handleChangeSelection("calculation", value);
              }}
            >
              {getCalculations(calculationResults, selection).map((calculation) => (
                <FormControlLabel
                  key={calculation.id}
                  value={calculation.id}
                  control={<Radio color="primary" />}
                  label={calculation.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    );
  }
}
